import { Button, Table } from "@vjal/ui";
import { useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { getRoleDefinition, isCoachingRole, type CoachingRole } from "../../domain/roles";
import { useCoachingProfile } from "../../hooks/useCoachingQueries";
import { switchCoachingOrganization } from "../../services/coaching-org-switch";
import type { CoachingMembershipDto } from "../../services/coaching-dtos";

type CoachingMembership = CoachingMembershipDto & { coachingRole: CoachingRole };

export function CoachingOrgOverview() {
  const profile = useCoachingProfile();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const memberships = useMemo(
    () =>
      (profile.data?.memberships ?? [])
        .filter((membership): membership is CoachingMembership => isCoachingRole(membership.coachingRole))
        .sort((left, right) => displayOrgName(left).localeCompare(displayOrgName(right), undefined, { sensitivity: "base" })),
    [profile.data?.memberships]
  );
  if (memberships.length <= 1) return null;

  function selectMembership(membership: CoachingMembership) {
    void switchCoachingOrganization({
      membership,
      queryClient,
      profile: profile.data,
      navigate,
    });
  }

  return (
    <section aria-labelledby="coaching-organisations-heading" className="rounded-xl border border-line bg-white p-4">
      <div className="mb-3">
        <h2 id="coaching-organisations-heading" className="m-0 text-base font-semibold text-ink-heading">
          Your coaching organisations
        </h2>
        <p className="m-0 mt-1 text-sm text-ink-muted">Switch the organisation whose coaching records you are viewing.</p>
      </div>
      <Table
        ariaLabel="Your coaching organisations"
        className="-mx-4 sm:mx-0"
        columns={[
          { key: "orgName", label: "Organisation", priority: 1, render: displayOrgName },
          { key: "coachingRole", label: "Your role", priority: 2, render: (membership) => getRoleDefinition(membership.coachingRole).label },
          {
            key: "switch",
            label: "",
            priority: 2,
            className: "w-24",
            render: (membership) => (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                aria-label={`Switch to ${displayOrgName(membership)}`}
                onClick={() => selectMembership(membership)}
              >
                Switch
              </Button>
            )
          }
        ]}
        data={memberships}
        getRowKey={(membership) => membership.orgId}
      />
    </section>
  );
}

function displayOrgName(membership: CoachingMembershipDto) {
  return membership.orgName?.trim() || `Organisation ${membership.orgId}`;
}
